import { useEffect, useState } from 'react'
import type { FetchPickerOptions, PickerOption } from '@/lib/projectOptions'

const DEBOUNCE_MS = 250

interface UseOptionsSourceArgs {
  fetchOptions: FetchPickerOptions
  source: string
  group: string | null
  searchText: string | null
  enabled: boolean
}

/**
 * Fetches options for one combobox while it is open, debounced per keystroke.
 * Options only ever assist: a failed or empty fetch resolves to no options and
 * the field carries on as free text.
 */
export function useOptionsSource({
  fetchOptions,
  source,
  group,
  searchText,
  enabled,
}: UseOptionsSourceArgs): PickerOption[] {
  const [options, setOptions] = useState<PickerOption[]>([])

  useEffect(() => {
    if (!enabled) {
      // eslint-disable-next-line react-hooks/set-state-in-effect -- clear on close so a reopen never flashes the previous list
      setOptions([])
      return
    }
    const controller = new AbortController()
    const timer = setTimeout(() => {
      fetchOptions({ source, group, searchText }, controller.signal)
        .then((next) => {
          if (controller.signal.aborted) return
          setOptions(next)
        })
        .catch((err: unknown) => {
          if (controller.signal.aborted) return
          if (err instanceof DOMException && err.name === 'AbortError') return
          setOptions([])
        })
    }, DEBOUNCE_MS)
    return () => {
      clearTimeout(timer)
      controller.abort()
    }
  }, [fetchOptions, source, group, searchText, enabled])

  return options
}
